import {
    ArrowBackOutlined,
    HistoryOutlined,
    ChevronRightOutlined,
    CloudUploadOutlined,
    CreateNewFolderOutlined,
    DeleteOutline,
    DownloadOutlined,
    DriveFileMoveOutlined,
    DriveFileRenameOutlineOutlined,
    FolderOpenOutlined,
    FolderOutlined,
    LinkRounded,
    MoreVertOutlined,
    SearchOutlined,
} from "@mui/icons-material";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputAdornment,
    Link,
    Menu,
    MenuItem,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";

import { colors, palette, semantic } from "../theme/tokens";

import {
    BISON_FOLDER,
    DEFAULT_FOLDER,
    customFolderId,
    fileTypeLabel,
    folderSlug,
    formatBytes,
    formatShortDate,
    guessMarketplace,
    templateKey,
    type DocumentFile,
    type DocumentsModel,
    type FolderId,
    type FolderSummary,
    type ProvidedTemplate,
} from "./model";
import { useDocumentActivity, type FilePatch, type RoomEvent } from "./useDocuments";

// Dropbox-style file browser (2026-09-06, Bill; table layout and custom
// folders the day after). A light gray toolbar (breadcrumb, search, Folders /
// All files, "New folder" at the top level, a plain "Upload" inside a folder)
// sits over the body. At the top level the body is the folder list: name
// (regular weight since 2026-09-08, Bill) plus a light gray count when there
// is one, no subtitle, no "empty", no hover button. Inside a folder (and in All files or a search) the body is a table:
// File name, Folder (All files only), Type, Added by, Date added, Size, then
// the row menu (Download, Rename, Move to, Delete). An Activity view lists
// every change made on the page (the room event log, `document_*` and
// `folder_*` types), newest first. No icon before a
// file name; the header is #EBEBEB and row hover #F9F9F9; file names are
// regular weight and never underline. An empty folder shows a centred icon and one line. Opening a
// folder swaps the body in place; the page never scrolls or navigates.
// Rename is inline and only changes the display name (the stored file and its
// URL never move).
//
// Every row ends with a copy-link button (2026-09-08, Bill): the link is this
// page's URL plus `?folder=<id>&file=<key>`, where the key is an upload's
// stored name or a template's file name. Opening it lands on the Documents
// page with that folder open and the row highlighted (Documents.tsx reads the
// query). A query string, not a hash: the sign-in flow carries
// `pathname + search` through the magic link and the AAD bridge and drops the
// hash, so a hash link would break for anyone who has to sign in first. Bison
// template rows pair the link with a Download button (it was "Open in a new
// tab"; these files are downloaded, not read in the browser).
//
// Mobile (below md): rows are icon, name and one caption line, plus the menu
// button. No columns.

export type BrowserView = { kind: "root" } | { kind: "folder"; folder: FolderId } | { kind: "all" } | { kind: "activity" };

/** Shareable link to one file on this page. */
// Links name the folder by its label ("product-data"), not its stored id
// ("product-spreadsheet"); Documents.tsx resolves either, so older links still open.
function fileLink(folderLabel: string, key: string): string {
    return `${folderLink(folderLabel)}&file=${encodeURIComponent(key)}`;
}

/** Shareable link that opens one folder on this page. */
function folderLink(folderLabel: string): string {
    const { origin, pathname } = window.location;
    return `${origin}${pathname}?folder=${encodeURIComponent(folderSlug(folderLabel))}`;
}

function CopyLinkButton({ url, name }: { url: string; name: string }) {
    const [copied, setCopied] = useState(false);
    const copy = () => {
        navigator.clipboard?.writeText(url).then(
            () => {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1500);
            },
            () => undefined,
        );
    };
    return (
        <Tooltip title={copied ? "Link copied" : "Copy link"} disableInteractive>
            <IconButton size="small" onClick={copy} aria-label={`Copy link to ${name}`}>
                <LinkRounded fontSize="small" />
            </IconButton>
        </Tooltip>
    );
}

/** Scrolls the row into view when it is the one a deep link named. */
function useHighlightedRow(active: boolean) {
    const ref = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        if (!active) return;
        const t = window.setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
        return () => window.clearTimeout(t);
    }, [active]);
    return ref;
}

const HIGHLIGHT_SX = { bgcolor: palette.infoBg, "&:hover": { bgcolor: palette.infoBg } } as const;

export interface FileBrowserProps {
    slug: string;
    model: DocumentsModel;
    view: BrowserView;
    onView: (view: BrowserView) => void;
    search: string;
    onSearch: (q: string) => void;
    staff: boolean;
    onUploadInto: (folder: FolderId) => void;
    onPatch: (name: string, patch: FilePatch) => Promise<unknown>;
    onRemove: (name: string) => Promise<unknown>;
    onCreateFolder: (id: FolderId, label: string) => Promise<unknown>;
    busy?: boolean;
    /** File key a deep link named (an upload's stored name or a template's file name); that row is highlighted. */
    highlight?: string | null;
}

// Every row (folders, files, templates) is 48px with the name at 15px / 23px
// (Bill, 2026-09-08: the folder names went to the drawer's 13px, then 15% up
// because that read too small; 44px rows, then 10% more; and the file rows
// were then matched to the folder rows so the two views read as one table).
const ROW_MIN_HEIGHT = 48;
const NAME_SX = { fontWeight: 400, color: colors.navy, fontSize: 15, lineHeight: "23px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } as const;
const CELL_PX = { xs: 1.5, md: 2 } as const;
// Bill, 2026-09-07: toolbar #EBEBEB, row hover #F9F9F9. The column header row
// under the toolbar is a lighter gray so it does not read as the same band
// (Bill, 2026-09-08).
const HEADER_BG = "#EBEBEB";
const SUBHEADER_BG = "#F4F4F4";
const HOVER_BG = "#F9F9F9";

// Column tracks: name | (folder) | type | added by | date | size | link + menu.
function gridColumns(showFolder: boolean): string {
    return `minmax(0, 1fr) ${showFolder ? "170px " : ""}110px 140px 100px 72px 80px`;
}

export function FileBrowser(props: FileBrowserProps) {
    const { model, view, onView, search, onSearch, onUploadInto, onCreateFolder } = props;
    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down("md"));
    const q = search.trim().toLowerCase();
    const [newFolderOpen, setNewFolderOpen] = useState(false);

    const allFiles = useMemo(() => {
        const list = model.folders.flatMap((f) => f.files);
        return list.sort((a, b) => b.uploadedAt.localeCompare(a.uploadedAt));
    }, [model]);

    const searching = q.length > 0;
    const searchHits = useMemo(() => {
        if (!searching) return null;
        const files = allFiles.filter((f) => f.displayName.toLowerCase().includes(q) || (f.marketplace ?? "").toLowerCase().includes(q));
        const templates = model.templates.filter((t) => t.name.toLowerCase().includes(q));
        return { files, templates };
    }, [allFiles, model.templates, q, searching]);

    const folder = view.kind === "folder" ? model.folderById[view.folder] : undefined;

    return (
        <Box id="documents-folders" sx={{ border: `1px solid ${colors.line}`, borderRadius: "10px", bgcolor: colors.white, overflow: "hidden" }}>
            {/* Toolbar: breadcrumb, search, view toggle, new folder / upload */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flexWrap: "wrap",
                    px: CELL_PX,
                    py: 1.25,
                    bgcolor: HEADER_BG,
                    borderBottom: `1px solid ${colors.line}`,
                }}
            >
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: "1 1 auto", minWidth: 0 }}>
                    {view.kind !== "root" && (
                        <IconButton size="small" onClick={() => onView({ kind: "root" })} aria-label="Back to all folders" sx={{ mr: 0.5 }}>
                            <ArrowBackOutlined fontSize="small" />
                        </IconButton>
                    )}
                    <Crumb active={view.kind === "root"} onClick={() => onView({ kind: "root" })}>
                        Documents
                    </Crumb>
                    {folder && (
                        <>
                            <ChevronRightOutlined sx={{ fontSize: 18, color: colors.mediumGray }} />
                            <Crumb active>{folder.label}</Crumb>
                        </>
                    )}
                    {view.kind === "all" && (
                        <>
                            <ChevronRightOutlined sx={{ fontSize: 18, color: colors.mediumGray }} />
                            <Crumb active>All files</Crumb>
                        </>
                    )}
                    {view.kind === "activity" && (
                        <>
                            <ChevronRightOutlined sx={{ fontSize: 18, color: colors.mediumGray }} />
                            <Crumb active>Activity</Crumb>
                        </>
                    )}
                </Stack>
                <TextField
                    size="small"
                    placeholder="Search files"
                    value={search}
                    onChange={(e) => onSearch(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlined fontSize="small" />
                                </InputAdornment>
                            ),
                        },
                    }}
                    sx={{ width: { xs: "100%", sm: 220 }, "& .MuiInputBase-root": { height: 36, fontSize: "0.875rem", bgcolor: colors.white } }}
                />
                {!mobile && (
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={view.kind === "all" ? "all" : view.kind === "activity" ? "activity" : "folders"}
                        onChange={(_, v) => {
                            if (v === "all") onView({ kind: "all" });
                            else if (v === "activity") onView({ kind: "activity" });
                            else if (v === "folders") onView({ kind: "root" });
                        }}
                        sx={{ bgcolor: colors.white, "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 600, px: 1.5, py: 0.5, fontSize: "0.8rem" } }}
                    >
                        <ToggleButton value="folders">Folders</ToggleButton>
                        <ToggleButton value="all">All files</ToggleButton>
                        <ToggleButton value="activity">Activity</ToggleButton>
                    </ToggleButtonGroup>
                )}
                {view.kind === "root" && !searching && (
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<CreateNewFolderOutlined fontSize="small" />}
                        onClick={() => setNewFolderOpen(true)}
                        sx={{ textTransform: "none", fontWeight: 600, bgcolor: colors.white }}
                    >
                        New folder
                    </Button>
                )}
                {folder && !folder.readOnly && (
                    <Button
                        size="small"
                        variant="contained"
                        color="secondary"
                        startIcon={<CloudUploadOutlined fontSize="small" />}
                        onClick={() => onUploadInto(folder.id)}
                        sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                        Upload
                    </Button>
                )}
            </Box>

            {/* A steady minimum height so a short folder does not shrink the page and
                pull the scroll position with it; the empty state centres in it. */}
            <Box sx={{ minHeight: { md: 360 }, display: "flex", flexDirection: "column" }}>
                {searchHits ? (
                    <SearchResults hits={searchHits} q={search} {...props} mobile={mobile} />
                ) : view.kind === "root" ? (
                    <RootView {...props} mobile={mobile} />
                ) : view.kind === "all" ? (
                    <FileTable files={allFiles} showFolder {...props} mobile={mobile} />
                ) : view.kind === "activity" ? (
                    <ActivityView slug={props.slug} model={model} mobile={mobile} />
                ) : folder ? (
                    folder.readOnly ? (
                        <TemplateTable templates={folder.templates} mobile={mobile} highlight={props.highlight} />
                    ) : (
                        <FileTable files={folder.files} {...props} mobile={mobile} />
                    )
                ) : null}
            </Box>

            <NewFolderDialog
                open={newFolderOpen}
                existing={model.folders}
                onClose={() => setNewFolderOpen(false)}
                onCreate={async (label) => {
                    const id = customFolderId(label);
                    await onCreateFolder(id, label);
                    setNewFolderOpen(false);
                    onView({ kind: "folder", folder: id });
                }}
            />
        </Box>
    );
}

function Crumb({ children, active, onClick }: { children: React.ReactNode; active?: boolean; onClick?: () => void }) {
    if (active || !onClick) {
        return (
            <Typography sx={{ fontWeight: 700, color: colors.navy, fontSize: "0.95rem", whiteSpace: "nowrap" }}>{children}</Typography>
        );
    }
    return (
        <Link component="button" type="button" onClick={onClick} underline="hover" sx={{ fontWeight: 600, fontSize: "0.95rem", color: colors.mediumGray }}>
            {children}
        </Link>
    );
}

// ─── New folder ────────────────────────────────────────────────────────

function NewFolderDialog({
    open,
    existing,
    onClose,
    onCreate,
}: {
    open: boolean;
    existing: FolderSummary[];
    onClose: () => void;
    onCreate: (label: string) => Promise<unknown>;
}) {
    const [label, setLabel] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const trimmed = label.trim();
    const clash = existing.find((f) => f.label.toLowerCase() === trimmed.toLowerCase() || f.id === customFolderId(trimmed));

    const submit = async () => {
        if (!trimmed || clash) return;
        setSaving(true);
        setError(null);
        try {
            await onCreate(trimmed);
            setLabel("");
        } catch (err) {
            setError((err as Error).message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={() => {
                if (!saving) onClose();
            }}
            maxWidth="xs"
            fullWidth
        >
            <DialogTitle sx={{ fontSize: "1.1rem" }}>New folder</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    fullWidth
                    size="small"
                    label="Folder name"
                    value={label}
                    onChange={(e) => setLabel(e.target.value.slice(0, 60))}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") void submit();
                    }}
                    error={Boolean(clash) || Boolean(error)}
                    helperText={clash ? "A folder with that name already exists." : (error ?? " ")}
                    sx={{ mt: 1 }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>
                    Cancel
                </Button>
                <Button variant="contained" color="secondary" disabled={!trimmed || Boolean(clash) || saving} onClick={() => void submit()} sx={{ textTransform: "none", fontWeight: 600 }}>
                    Create
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ─── Root: the folder list ─────────────────────────────────────────────

// The folder list is a table like the file table it opens into (Bill,
// 2026-09-08): a header band, then Name | Files | chevron. The count is a
// cell ("3 files"), blank at zero, and sits close to the name: the name
// column takes two fifths of the free width (a fifth first, "80% closer than
// the far right", then Bill moved the column 20% of the width to the right;
// a floor so "Letters of authorization" never truncates) and a spacer takes
// the rest. Last added was removed the same day. On a phone the count stays
// inline.
const FOLDER_COLUMNS = "minmax(260px, 2fr) 90px minmax(0, 3fr) 24px";

function fileCountLabel(count: number): string {
    if (count === 0) return "";
    return count === 1 ? "1 file" : `${count} files`;
}

function RootView({ model, onView, mobile }: FileBrowserProps & { mobile: boolean }) {
    return (
        <Box>
            {!mobile && (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: FOLDER_COLUMNS,
                        alignItems: "center",
                        columnGap: 2,
                        px: CELL_PX,
                        py: 1,
                        bgcolor: SUBHEADER_BG,
                        borderBottom: `1px solid ${colors.lineSoft}`,
                    }}
                >
                    <HeaderCell>Name</HeaderCell>
                    <HeaderCell>Files</HeaderCell>
                    <span />
                    <span />
                </Box>
            )}
            {model.folders.map((f) => (
                <FolderRow key={f.id} folder={f} mobile={mobile} onOpen={() => onView({ kind: "folder", folder: f.id })} />
            ))}
        </Box>
    );
}

function FolderRow({ folder, mobile, onOpen }: { folder: FolderSummary; mobile: boolean; onOpen: () => void }) {
    // The row itself opens the folder, so the link button must not bubble.
    const copyLink = (
        <Box onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} sx={{ justifySelf: "end" }}>
            <CopyLinkButton url={folderLink(folder.label)} name={folder.label} />
        </Box>
    );
    return (
        <Box
            sx={{
                display: mobile ? "flex" : "grid",
                gridTemplateColumns: mobile ? undefined : FOLDER_COLUMNS,
                alignItems: "center",
                gap: 1.5,
                columnGap: 2,
                px: CELL_PX,
                minHeight: ROW_MIN_HEIGHT,
                cursor: "pointer",
                "& + &": { borderTop: `1px solid ${colors.lineSoft}` },
                "&:hover": { bgcolor: HOVER_BG },
            }}
            onClick={onOpen}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onOpen();
                }
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
                <FolderOutlined sx={{ color: colors.navy, fontSize: 24, flexShrink: 0 }} />
                <Typography sx={{ minWidth: 0, ...NAME_SX }}>
                    {folder.label}
                    {mobile && folder.count > 0 && (
                        <Typography component="span" sx={{ color: colors.mediumGray, fontWeight: 500, ml: 1, fontSize: 14 }}>
                            {folder.count}
                        </Typography>
                    )}
                </Typography>
            </Stack>
            {!mobile && (
                <>
                    <Typography variant="body2" sx={{ color: "text.secondary", whiteSpace: "nowrap", fontSize: 14 }}>
                        {fileCountLabel(folder.count)}
                    </Typography>
                    {copyLink}
                </>
            )}
            {mobile && copyLink}
            <ChevronRightOutlined sx={{ color: colors.mediumGray, justifySelf: "end" }} />
        </Box>
    );
}

// ─── Table pieces ──────────────────────────────────────────────────────

function HeaderCell({ children, align = "left" }: { children: React.ReactNode; align?: "left" | "right" }) {
    return (
        <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: colors.mediumGray, textTransform: "uppercase", letterSpacing: "0.04em", fontSize: "0.7rem", textAlign: align, whiteSpace: "nowrap" }}
        >
            {children}
        </Typography>
    );
}

function TableHeader({ showFolder }: { showFolder: boolean }) {
    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: gridColumns(showFolder),
                alignItems: "center",
                columnGap: 2,
                px: CELL_PX,
                py: 1,
                bgcolor: SUBHEADER_BG,
                borderBottom: `1px solid ${colors.lineSoft}`,
            }}
        >
            <HeaderCell>File name</HeaderCell>
            {showFolder && <HeaderCell>Folder</HeaderCell>}
            <HeaderCell>Type</HeaderCell>
            <HeaderCell>Added by</HeaderCell>
            <HeaderCell>Date added</HeaderCell>
            <HeaderCell align="right">Size</HeaderCell>
            <span />
        </Box>
    );
}

// Centred in the body's minimum height: an icon and one line.
function EmptyState({ text, icon }: { text: string; icon?: React.ReactNode }) {
    return (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", px: CELL_PX, py: 6, textAlign: "center" }}>
            <Box>
                {icon ?? <FolderOpenOutlined sx={{ fontSize: 44, color: colors.mediumGray, mb: 1 }} />}
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {text}
                </Typography>
            </Box>
        </Box>
    );
}

// ─── Bison templates ───────────────────────────────────────────────────

function TemplateTable({ templates, mobile, highlight }: { templates: ProvidedTemplate[]; mobile: boolean; highlight?: string | null }) {
    if (templates.length === 0) return <EmptyState text="No templates here yet." />;
    const columns = mobile ? "minmax(0, 1fr) 80px" : "minmax(0, 1fr) 110px 140px 80px";
    return (
        <Box>
            {!mobile && (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: columns,
                        alignItems: "center",
                        columnGap: 2,
                        px: CELL_PX,
                        py: 1,
                        bgcolor: SUBHEADER_BG,
                        borderBottom: `1px solid ${colors.lineSoft}`,
                    }}
                >
                    <HeaderCell>File name</HeaderCell>
                    <HeaderCell>Type</HeaderCell>
                    <HeaderCell>Added by</HeaderCell>
                    <span />
                </Box>
            )}
            {templates.map((t) => (
                <TemplateRow key={t.url} template={t} columns={columns} mobile={mobile} highlighted={highlight === templateKey(t)} />
            ))}
        </Box>
    );
}

function TemplateRow({ template: t, columns, mobile, highlighted }: { template: ProvidedTemplate; columns: string; mobile: boolean; highlighted: boolean }) {
    const ref = useHighlightedRow(highlighted);
    return (
        <Box
            ref={ref}
            sx={{
                display: "grid",
                gridTemplateColumns: columns,
                alignItems: "center",
                columnGap: 2,
                px: CELL_PX,
                minHeight: ROW_MIN_HEIGHT,
                "& + &": { borderTop: `1px solid ${colors.lineSoft}` },
                "&:hover": { bgcolor: HOVER_BG },
                ...(highlighted ? HIGHLIGHT_SX : {}),
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
                <Link href={t.url} target="_blank" rel="noopener noreferrer" underline="none" sx={NAME_SX}>
                    {t.name}
                </Link>
            </Stack>
            {!mobile && (
                <>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {fileTypeLabel(t.url)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Bison
                    </Typography>
                </>
            )}
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ justifySelf: "end" }}>
                <CopyLinkButton url={fileLink(BISON_FOLDER.label, templateKey(t))} name={t.name} />
                <Tooltip title="Download" disableInteractive>
                    <IconButton component="a" href={t.url} download size="small" aria-label={`Download ${t.name}`}>
                        <DownloadOutlined fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Stack>
        </Box>
    );
}

// ─── Search ────────────────────────────────────────────────────────────

function SearchResults(props: FileBrowserProps & { mobile: boolean; hits: { files: DocumentFile[]; templates: ProvidedTemplate[] }; q: string }) {
    const { hits, q, mobile } = props;
    if (hits.files.length === 0 && hits.templates.length === 0) {
        return <EmptyState text={`Nothing matches "${q.trim()}".`} icon={<SearchOutlined sx={{ fontSize: 44, color: colors.mediumGray, mb: 1 }} />} />;
    }
    return (
        <Box>
            {hits.files.length > 0 && <FileTable files={hits.files} showFolder {...props} />}
            {hits.templates.length > 0 && (
                <>
                    {hits.files.length > 0 && <Divider />}
                    <TemplateTable templates={hits.templates} mobile={mobile} highlight={props.highlight} />
                </>
            )}
        </Box>
    );
}

// ─── File table ────────────────────────────────────────────────────────

function FileTable(props: FileBrowserProps & { mobile: boolean; files: DocumentFile[]; showFolder?: boolean }) {
    const { files, mobile, showFolder } = props;
    if (files.length === 0) return <EmptyState text="No files here yet. Drop files above or use Upload." />;
    return (
        <Box>
            {!mobile && <TableHeader showFolder={Boolean(showFolder)} />}
            {files.map((f) => (
                <FileRow key={f.name} file={f} {...props} />
            ))}
        </Box>
    );
}

function FileRow(props: FileBrowserProps & { mobile: boolean; file: DocumentFile; showFolder?: boolean }) {
    const { file, mobile, showFolder, model, onPatch, onRemove, busy, highlight } = props;
    const highlighted = highlight === file.name;
    const rowRef = useHighlightedRow(highlighted);
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [askDelete, setAskDelete] = useState(false);
    const [moveAnchor, setMoveAnchor] = useState<HTMLElement | null>(null);
    const [renaming, setRenaming] = useState(false);
    const [draft, setDraft] = useState(file.displayName);
    const [error, setError] = useState<string | null>(null);

    const currentFolder = model.folderById[file.folder ?? DEFAULT_FOLDER] ?? model.folderById[DEFAULT_FOLDER];
    const addedBy = file.uploadedByName ?? file.uploadedBy ?? "";
    const targets = model.folders.filter((f) => !f.readOnly);

    const run = async (p: Promise<unknown>) => {
        setError(null);
        try {
            await p;
        } catch (err) {
            setError((err as Error).message);
        }
    };

    const commitRename = () => {
        const next = draft.trim();
        setRenaming(false);
        if (!next || next === file.displayName) {
            setDraft(file.displayName);
            return;
        }
        void run(onPatch(file.name, { displayName: next }));
    };

    const moveTo = (folder: FolderId) => {
        setMoveAnchor(null);
        setMenuAnchor(null);
        const patch: FilePatch = { folder };
        if (folder === "loa" && !file.marketplace) {
            patch.marketplace = guessMarketplace(file.displayName, model.loaMarketplaces);
        }
        void run(onPatch(file.name, patch));
    };

    const name = renaming ? (
        <TextField
            size="small"
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
                if (e.key === "Enter") commitRename();
                if (e.key === "Escape") {
                    setDraft(file.displayName);
                    setRenaming(false);
                }
            }}
            slotProps={{ htmlInput: { "aria-label": "File name" } }}
            sx={{ width: "100%", maxWidth: 480, "& .MuiInputBase-root": { height: 32, fontSize: "0.9rem" } }}
        />
    ) : (
        <Link
            href={file.url}
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
            onDoubleClick={(e) => {
                e.preventDefault();
                setRenaming(true);
            }}
            sx={{ display: "block", ...NAME_SX }}
        >
            {file.displayName}
        </Link>
    );

    const errorLine = error ? (
        <Typography variant="caption" sx={{ display: "block", color: "error.main", mt: 0.25 }}>
            {error}
        </Typography>
    ) : null;

    const menuButton = (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ justifySelf: "end" }}>
            <CopyLinkButton url={fileLink(currentFolder?.label ?? "Uploads", file.name)} name={file.displayName} />
            <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label={`Actions for ${file.displayName}`} disabled={busy}>
                <MoreVertOutlined fontSize="small" />
            </IconButton>
        </Stack>
    );

    const menus = (
        <>
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                <MenuItem component="a" href={`${file.url}?download`} onClick={() => setMenuAnchor(null)}>
                    <DownloadOutlined fontSize="small" sx={{ mr: 1.5 }} /> Download
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setMenuAnchor(null);
                        setDraft(file.displayName);
                        setRenaming(true);
                    }}
                >
                    <DriveFileRenameOutlineOutlined fontSize="small" sx={{ mr: 1.5 }} /> Rename
                </MenuItem>
                <MenuItem onClick={(e) => setMoveAnchor(e.currentTarget)}>
                    <DriveFileMoveOutlined fontSize="small" sx={{ mr: 1.5 }} /> Move to
                    <ChevronRightOutlined fontSize="small" sx={{ ml: "auto", color: colors.mediumGray }} />
                </MenuItem>
                <Divider />
                <MenuItem
                    onClick={() => {
                        setMenuAnchor(null);
                        setAskDelete(true);
                    }}
                    sx={{ color: semantic.danger.text }}
                >
                    <DeleteOutline fontSize="small" sx={{ mr: 1.5 }} /> Delete
                </MenuItem>
            </Menu>

            {/* Deleting a file cannot be undone, so it asks first (2026-10-27, Elijah). */}
            <Dialog open={askDelete} onClose={() => setAskDelete(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontSize: "1.1rem" }}>Are you sure you want to delete?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: "0.95rem" }}>
                        {file.displayName} will be removed from this room for everyone. This cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAskDelete(false)} sx={{ textTransform: "none" }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => {
                            setAskDelete(false);
                            void run(onRemove(file.name));
                        }}
                        sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Menu anchorEl={moveAnchor} open={Boolean(moveAnchor)} onClose={() => setMoveAnchor(null)} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
                {targets.map((f) => (
                    <MenuItem key={f.id} selected={f.id === currentFolder?.id} onClick={() => moveTo(f.id)}>
                        {f.label}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );

    const rowSx = {
        px: CELL_PX,
        minHeight: ROW_MIN_HEIGHT,
        "& + &": { borderTop: `1px solid ${colors.lineSoft}` },
        "&:hover": { bgcolor: HOVER_BG },
        ...(highlighted ? HIGHLIGHT_SX : {}),
    } as const;

    if (mobile) {
        return (
            <Box ref={rowRef} sx={{ ...rowSx, display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {name}
                    <Typography variant="caption" sx={{ display: "block", color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {fileTypeLabel(file.displayName, file.contentType)} · {formatShortDate(file.uploadedAt)}
                        {addedBy ? ` · ${addedBy}` : ""}
                        {showFolder && currentFolder ? ` · ${currentFolder.label}` : ""}
                    </Typography>
                    {errorLine}
                </Box>
                {menuButton}
                {menus}
            </Box>
        );
    }

    return (
        <Box ref={rowRef} sx={{ ...rowSx, display: "grid", gridTemplateColumns: gridColumns(Boolean(showFolder)), alignItems: "center", columnGap: 2, py: 0.75 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    {name}
                    {errorLine}
                </Box>
            </Stack>
            {showFolder && (
                <Typography variant="body2" sx={{ color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {currentFolder?.label ?? ""}
                </Typography>
            )}
            <Typography variant="body2" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                {fileTypeLabel(file.displayName, file.contentType)}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {addedBy}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                {formatShortDate(file.uploadedAt)}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", whiteSpace: "nowrap", textAlign: "right" }}>
                {formatBytes(file.size)}
            </Typography>
            {menuButton}
            {menus}
        </Box>
    );
}

// ─── Activity ──────────────────────────────────────────────────────────
//
// Every change made on the page, newest first, from the room event log:
// uploads, renames, moves, deletions and folder changes. Each line reads
// "<who> <did what> <to which file>" with the date on the right.

function folderLabel(model: DocumentsModel, id: unknown): string {
    if (typeof id !== "string" || !id) return "Uploads";
    return model.folderById[id]?.label ?? id.replace(/^f-/, "").replace(/-/g, " ");
}

function describeEvent(e: RoomEvent, model: DocumentsModel): string {
    const p = e.payload ?? {};
    const who = (typeof p.byName === "string" && p.byName) || e.by || "Someone";
    const file = (typeof p.displayName === "string" && p.displayName) || (typeof p.name === "string" && p.name) || "a file";
    switch (e.type) {
        case "document_uploaded":
            return `${who} uploaded ${file} to ${folderLabel(model, p.folder)}`;
        case "document_replaced":
            return `${who} replaced ${file}`;
        case "document_renamed":
            return `${who} renamed ${String(p.from ?? file)} to ${String(p.to ?? file)}`;
        case "document_moved":
            return `${who} moved ${file} from ${folderLabel(model, p.from)} to ${folderLabel(model, p.to)}`;
        case "document_removed":
            return `${who} deleted ${file}${p.folder ? ` from ${folderLabel(model, p.folder)}` : ""}`;
        case "document_returned":
            return `${who} sent ${file} back${typeof p.note === "string" && p.note ? `: ${p.note}` : ""}`;
        case "document_review_cleared":
            return `${who} cleared the note on ${file}`;
        case "folder_created":
            return `${who} created the folder ${String(p.label ?? "")}`;
        case "folder_renamed":
            return `${who} renamed the folder ${String(p.from ?? "")} to ${String(p.label ?? "")}`;
        case "folder_removed":
            return `${who} deleted the folder ${String(p.label ?? "")}`;
        default:
            return `${who} changed ${file}`;
    }
}

function formatDateTime(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const sameYear = d.getFullYear() === new Date().getFullYear();
    return d.toLocaleString("en-US", { month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" }), hour: "numeric", minute: "2-digit" });
}

function ActivityView({ slug, model, mobile }: { slug: string; model: DocumentsModel; mobile: boolean }) {
    const activity = useDocumentActivity(slug);
    if (activity.isLoading) return <EmptyState text="Loading…" icon={<HistoryOutlined sx={{ fontSize: 44, color: colors.mediumGray, mb: 1 }} />} />;
    if (activity.error) return <EmptyState text="Could not load the activity log." icon={<HistoryOutlined sx={{ fontSize: 44, color: colors.mediumGray, mb: 1 }} />} />;
    const events = activity.data ?? [];
    if (events.length === 0) return <EmptyState text="No changes yet. Uploads, renames, moves and deletions will show here." icon={<HistoryOutlined sx={{ fontSize: 44, color: colors.mediumGray, mb: 1 }} />} />;
    return (
        <Box>
            {!mobile && (
                <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 150px", alignItems: "center", columnGap: 2, px: CELL_PX, py: 1, bgcolor: SUBHEADER_BG, borderBottom: `1px solid ${colors.lineSoft}` }}>
                    <HeaderCell>Change</HeaderCell>
                    <HeaderCell align="right">When</HeaderCell>
                </Box>
            )}
            {events.map((e, i) => (
                <Box
                    key={`${e.at}-${i}`}
                    sx={{
                        display: "grid",
                        gridTemplateColumns: mobile ? "minmax(0, 1fr)" : "minmax(0, 1fr) 150px",
                        alignItems: "center",
                        columnGap: 2,
                        px: CELL_PX,
                        py: 1.25,
                        "& + &": { borderTop: `1px solid ${colors.lineSoft}` },
                        "&:hover": { bgcolor: HOVER_BG },
                    }}
                >
                    <Typography variant="body2" sx={{ color: colors.navy, overflowWrap: "anywhere" }}>
                        {describeEvent(e, model)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", whiteSpace: "nowrap", textAlign: mobile ? "left" : "right", fontSize: mobile ? "0.75rem" : undefined }}>
                        {formatDateTime(e.at)}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}
